import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * useFollow — follow-state + optimistic toggle for one entity (migration 101).
 *
 * Reads GET /api/follows/:type/:id for the current follow flag and exposes a
 * toggle() that POSTs (follow) / DELETEs (unfollow) with an OPTIMISTIC cache
 * update: the button flips instantly, rolls back on error, and re-syncs on
 * settle. Also invalidates ['my-follows'] so a "things I follow" surface (the
 * dashboard feed) refreshes when it exists.
 *
 * The follow row is always keyed to the signed-in user server-side, so this hook
 * never sends a user id.
 */

export type FollowableType = 'parcel' | 'property' | 'client' | 'appeal' | 'tax_bill' | 'invoice';

interface FollowState {
  following: boolean;
}

interface ToggleContext {
  prev: FollowState | undefined;
}

export interface UseFollowResult {
  following: boolean;
  isLoading: boolean;
  isPending: boolean;
  toggle: () => void;
}

export function useFollow(entityType: FollowableType, entityId: string | undefined): UseFollowResult {
  const qc = useQueryClient();
  const key = ['follow', entityType, entityId];

  const query = useQuery<FollowState>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<FollowState>(`/api/follows/${entityType}/${entityId}`, signal),
    enabled: !!entityId,
    staleTime: 30_000,
  });

  const mutation = useMutation<FollowState, unknown, boolean, ToggleContext>({
    mutationFn: async (next: boolean) => {
      if (next) await api.post(`/api/follows/${entityType}/${entityId}`);
      else await api.delete(`/api/follows/${entityType}/${entityId}`);
      return { following: next };
    },
    onMutate: async (next: boolean) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<FollowState>(key);
      qc.setQueryData<FollowState>(key, { following: next }); // optimistic flip
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx) qc.setQueryData<FollowState>(key, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['my-follows'] });
      // Refresh the follower count chip + roster (FollowButton reads a separate
      // ['followers', …] query, which 'follow' does not prefix-match).
      qc.invalidateQueries({ queryKey: ['followers', entityType, entityId] });
    },
  });

  const following = query.data?.following ?? false;
  return {
    following,
    isLoading: query.isLoading,
    isPending: mutation.isPending,
    toggle: () => {
      if (!entityId) return;
      mutation.mutate(!following);
    },
  };
}
